import React, { useState, useEffect, useRef } from 'react'
import {
  Check,
  X,
  Edit3,
  Calendar,
  Hash,
  Mail,
  Link as LinkIcon,
  Type,
  Phone,
  File,
  Image,
  Video,
  Music,
  Archive,
  FileText
} from 'lucide-react'
import NumberInput from '@/components/form/NumberInput'
import PhoneNumberInput from '@/components/PhoneNumberInput'
import { formatNumber } from '@/utils/numberFormat'
import FileDisplayCell from '@/components/FileDisplayCell'

const InlineEditCell = ({ 
  value, 
  attribute, 
  onSave, 
  onCancel,
  isEditing,
  onStartEdit 
}) => {
  const [editValue, setEditValue] = useState(value || '')
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      // Select all text for easy replacement
      if (inputRef.current.select) {
        inputRef.current.select()
      }
    }
  }, [isEditing])

  useEffect(() => {
    setEditValue(value || '')
  }, [value])

  const handleSave = async () => {
    setIsLoading(true)
    try {
      await onSave(editValue)
    } catch (error) {
      console.error('Error saving:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setEditValue(value || '')
    onCancel()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const formatDisplayValue = (val) => {
    if (val === null || val === undefined || val === '') return '-'

    switch (attribute.data_type) {
      case 'Boolean':
        return val ? 'Yes' : 'No'
      case 'Date':
        if (attribute.format) {
          return formatDateWithCustomFormat(val, attribute.format)
        }
        return new Date(val).toLocaleDateString()
      case 'Datetime':
        if (attribute.format) {
          return formatDateWithCustomFormat(val, attribute.format)
        }
        return new Date(val).toLocaleString()
      case 'Number':
        return formatNumber(val, true) // true = integer
      case 'Decimal':
        return formatNumber(val, false) // false = decimal
      case 'Phone Number':
        // Format phone number for display
        const digits = String(val).replace(/\D/g, '')
        if (digits.length <= 3) {
          return digits
        } else if (digits.length <= 6) {
          return `${digits.slice(0, 3)} ${digits.slice(3)}`
        } else if (digits.length <= 10) {
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
        } else {
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10)}`
        }
      case 'File':
      case 'Image':
      case 'Video':
      case 'Audio':
      case 'Document':
      case 'Archive':
        // For file types, return a special marker that will be handled by FileDisplayCell
        return { __fileId: val, __dataType: attribute.data_type }
      default:
        return String(val)
    }
  }

  const formatDateWithCustomFormat = (dateValue, format) => {
    try {
      const date = new Date(dateValue)
      if (isNaN(date.getTime())) return dateValue

      // Basic format conversion (can be enhanced with a proper date library)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                             'July', 'August', 'September', 'October', 'November', 'December']

      return format
        .replace('YYYY', year)
        .replace('MM', month)
        .replace('DD', day)
        .replace('HH', hours)
        .replace('mm', minutes)
        .replace('ss', seconds)
        .replace('MMMM', fullMonthNames[date.getMonth()])
        .replace('MMM', monthNames[date.getMonth()])
    } catch (error) {
      return dateValue
    }
  }

  const getFieldIcon = () => {
    switch (attribute.data_type) {
      case 'Email': return Mail
      case 'URL': return LinkIcon
      case 'Number':
      case 'Decimal': return Hash
      case 'Date':
      case 'Datetime': return Calendar
      case 'Phone Number': return Phone
      default: return Type
    }
  }

  const renderEditField = () => {
    const Icon = getFieldIcon()

    // If attribute has allowed_values, show dropdown
    if (attribute.allowed_values && attribute.allowed_values.length > 0) {
      return (
        <select
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">Select an option...</option>
          {attribute.allowed_values.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      )
    }

    // Otherwise, show input based on data type
    switch (attribute.data_type) {
      case 'Boolean':
        return (
          <select
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value === 'true')}
            onKeyDown={handleKeyDown}
            className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        )

      case 'Number':
        return (
          <NumberInput
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            allowDecimals={false}
            className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter number (e.g., 1,234)"
          />
        )

      case 'Decimal':
        return (
          <NumberInput
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            allowDecimals={true}
            className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter decimal (e.g., 1,234.56)"
          />
        )

      case 'Email':
        return (
          <input
            ref={inputRef}
            type="email"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter email"
          />
        )

      case 'URL':
        return (
          <input
            ref={inputRef}
            type="url"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="https://example.com"
          />
        )

      case 'Date':
        return (
          <input
            ref={inputRef}
            type="date"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        )

      case 'Datetime':
        return (
          <input
            ref={inputRef}
            type="datetime-local"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        )

      case 'Phone Number':
        return (
          <div className="relative">
            <input
              ref={inputRef}
              type="tel"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="0123456789"
            />
            <div className="absolute -bottom-6 left-0 text-xs text-gray-500">
              Must start with 0, min 8 digits
            </div>
          </div>
        )

      case 'File':
      case 'Image':
      case 'Video':
      case 'Audio':
      case 'Document':
      case 'Archive':
        // For file types, return the FileDisplayCell in editing mode
        return (
          <FileDisplayCell
            fileId={value}
            dataType={attribute.data_type}
            isEditing={true}
            onStartEdit={() => {}} // Already in editing mode
            onSave={onSave}
            onCancel={onCancel}
          />
        )

      default:
        return (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={`Enter ${attribute.label || attribute.name}`}
          />
        )
    }
  }

  if (isEditing) {
    // Check if this is a file type - if so, let FileDisplayCell handle the editing UI
    const isFileType = ['File', 'Image', 'Video', 'Audio', 'Document', 'Archive'].includes(attribute.data_type)

    if (isFileType) {
      return renderEditField() // This will return the FileDisplayCell
    }

    return (
      <div className="edit-container">
        <div className="edit-input">
          {renderEditField()}
        </div>
        <div className="edit-actions">
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
            title="Save"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
            title="Cancel"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
    )
  }

  // Check if this is a file type
  const isFileType = ['File', 'Image', 'Video', 'Audio', 'Document', 'Archive'].includes(attribute.data_type)

  if (isFileType) {
    return (
      <FileDisplayCell
        fileId={value}
        dataType={attribute.data_type}
        isEditing={isEditing}
        onStartEdit={onStartEdit}
        onSave={onSave}
        onCancel={onCancel}
      />
    )
  }

  return (
    <div
      className="group flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors min-w-0"
      onClick={onStartEdit}
    >
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm text-gray-900">
          {formatDisplayValue(value)}
        </div>
      </div>
      <div className="flex-shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Edit3 className="h-3 w-3 text-gray-400" />
      </div>
    </div>
  )
}

export default InlineEditCell
